import React, { useMemo, useState } from 'react';
import { LayoutChangeEvent, Platform, ScrollView, Text, View } from 'react-native';
import { FontFamily } from '../../../constants/fonts';
import ptExecutionSectionStyles from '../styles/PTExecutionSectionStyles';

let VictoryAxis: any,
  VictoryArea: any,
  VictoryChart: any,
  VictoryLine: any,
  VictoryScatter: any,
  VictoryTooltip: any,
  VictoryVoronoiContainer: any,
  VictoryLabel: any,
  VictoryTheme: any;

const assignVictoryModules = (pkg: any) => {
  ({
    VictoryAxis,
    VictoryArea,
    VictoryChart,
    VictoryLine,
    VictoryScatter,
    VictoryTooltip,
    VictoryVoronoiContainer,
    VictoryLabel,
    VictoryTheme,
  } = pkg);
};

if (Platform.OS === 'web') {
  assignVictoryModules(require('victory'));
} else {
  try {
    assignVictoryModules(require('victory-native'));
  } catch (error) {
    assignVictoryModules(require('victory'));
  }
}

export interface PTExecutionTrendPoint {
  label: string;       // X-axis label: '2024', 'P3', 'W2', etc.
  percentage: number;  // Y value
  highlighted?: boolean;
  subLabel?: string;   // Secondary label below tick: 'P2', "'26", etc.
}

interface PTExecutionTrendChartProps {
  data?: PTExecutionTrendPoint[];
  legendLabel?: string;
}

const victoryAxisLabelStyle = {
  fontSize: 12,
  lineHeight: 16,
  fill: '#777777',
  fontFamily: FontFamily.regular,
};

const victoryPointLabelStyle = {
  fontSize: 11,
  lineHeight: 14,
  fill: '#005CBC',
  fontFamily: FontFamily.bold,
};

const victoryIncrementLabelStyle = {
  fontSize: 12,
  lineHeight: 16,
  fill: '#0A8A1F',
  fontFamily: FontFamily.bold,
};

const victoryTooltipFlyoutStyle = {
  fill: '#1F2937',
  stroke: '#1F2937',
};

const victoryTooltipTextStyle = {
  fontSize: 12,
  lineHeight: 16,
  fill: '#FFFFFF',
  fontFamily: FontFamily.medium,
};

const PTExecutionTrendChart: React.FC<PTExecutionTrendChartProps> = ({
  data = [],
  legendLabel = 'Perfect Truck Execution',
}) => {
  const styles = ptExecutionSectionStyles;
  const isIOS = Platform.OS === 'ios';
  const [containerWidth, setContainerWidth] = useState(0);
  const axisLeftOffset = isIOS ? 44 : 54;

  const chartData = useMemo(
    () =>
      data.map((item, index) => ({
        x: index,
        y: item.percentage,
        tickLabel: item.label,
        subLabel: item.subLabel,
        highlighted: item.highlighted ?? false,
        // Stagger % labels vertically so adjacent point labels don't collide
        labelDy: index % 2 === 0 ? -14 : -28,
      })),
    [data]
  );

  const indexTicks = useMemo(() => chartData.map((item) => item.x), [chartData]);
  const hasSubLabels = useMemo(() => chartData.some((d) => d.subLabel != null), [chartData]);

  const maxY = useMemo(
    () => Math.max(60, Math.ceil((Math.max(...chartData.map((d) => d.y), 0) + 10) / 10) * 10),
    [chartData],
  );

  // Generate 4-5 evenly-spaced Y-axis ticks so labels never overlap
  const yTicks = useMemo(() => {
    const step = Math.ceil(maxY / 4);
    const raw = [0, step, step * 2, step * 3, maxY];
    return [...new Set(raw)].filter((v) => v <= maxY);
  }, [maxY]);

  const incrementLabels = useMemo(() => {
    if (chartData.length < 2) {
      return [];
    }
    // Compute increments from the rounded display values so the "pp" label
    // matches what the end-user sees (e.g., 4.5 → 5 and 27.1 → 27 yields +22pp).
    return chartData.slice(1).map((point, index) => {
      const previousPoint = chartData[index];
      const prevRounded = Math.round(previousPoint.y);
      const currRounded = Math.round(point.y);
      const pointDelta = currRounded - prevRounded;
      const deltaPrefix = pointDelta >= 0 ? '+' : '';

      return {
        x: (previousPoint.x + point.x) / 2,
        y: (previousPoint.y + point.y) / 2 + 2,
        label: `${deltaPrefix}${Math.round(pointDelta)}pp`,
      };
    });
  }, [chartData]);

  // Minimum pixels per data point so labels have breathing room
  const MIN_POINT_SPACING = 58;

  const chartWidth = useMemo(() => {
    const minFromPoints = Math.max(280, data.length * MIN_POINT_SPACING);
    if (containerWidth <= 0) return minFromPoints;
    return Math.max(containerWidth - 8, minFromPoints);
  }, [containerWidth, data.length]);

  // Only engage the horizontal scroll when the content exceeds the visible container
  const needsHScroll = containerWidth > 0 && chartWidth > containerWidth - 8;

  const onLayoutContainer = (event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  };

  return (
    <View style={styles.trendChartContainer} onLayout={onLayoutContainer}>
      <ScrollView
        horizontal
        scrollEnabled={needsHScroll}
        showsHorizontalScrollIndicator={needsHScroll}
        contentContainerStyle={{ flexGrow: 0 }}
        // Prevent the inner chart from intercepting vertical page scroll on web
        scrollEventThrottle={16}
      >
      <VictoryChart
        width={chartWidth}
        height={isIOS ? 184 : 220}
        domain={{
          x: [-0.5, Math.max(0, chartData.length - 1) + 0.5],
          y: [0, maxY],
        }}
        padding={
          isIOS
            ? { top: 24, right: 18, bottom: hasSubLabels ? 36 : 24, left: 44 }
            : { top: 28, right: 26, bottom: hasSubLabels ? 54 : 42, left: 54 }
        }
        theme={VictoryTheme.material}
      >
        <VictoryAxis
          dependentAxis
          orientation="left"
          crossAxis={false}
          offsetX={axisLeftOffset}
          tickValues={yTicks}
          tickFormat={(tick: number) => `${tick}%`}
          style={{
            axis: { stroke: '#D8D8D8', strokeWidth: 1 },
            ticks: { stroke: 'transparent', size: 0 },
            tickLabels: victoryAxisLabelStyle,
            grid: { stroke: '#ECEFF4', strokeWidth: 1, strokeDasharray: '6,6' },
          }}
        />

        <VictoryAxis
          tickValues={indexTicks}
          tickLabelComponent={
            <VictoryLabel
              text={({ datum }: any) => {
                const pt = chartData[datum];
                if (pt?.subLabel) return [pt.tickLabel, pt.subLabel];
                return [pt?.tickLabel ?? String(datum)];
              }}
              style={[
                { fontSize: 12, fill: '#777777', fontFamily: FontFamily.regular },
                { fontSize: 10, fill: '#9CA3AF', fontFamily: FontFamily.regular },
              ]}
              lineHeight={1.4}
            />
          }
          style={{
            axis: { stroke: '#D8D8D8', strokeWidth: 1 },
            ticks: { stroke: 'transparent', size: 0 },
            grid: { stroke: 'transparent' },
          }}
        />

        <VictoryArea
          data={chartData}
          interpolation="monotoneX"
          style={{
            data: {
              fill: '#D9DEE7',
              fillOpacity: 0.75,
              stroke: 'transparent',
            },
          }}
        />

        <VictoryLine
          data={chartData}
          interpolation="monotoneX"
          style={{
            data: {
              stroke: '#005CBC',
              strokeWidth: 3,
            },
          }}
        />

        <VictoryScatter
          data={chartData}
          size={({ datum }: any) => (datum.highlighted ? 9 : 6.5)}
          style={{
            data: {
              fill: ({ datum }: any) => (datum.y < 10 ? '#C12C01' : '#005CBC'),
              stroke: ({ datum }: any) => (datum.y < 10 ? '#C12C01' : '#005CBC'),
              strokeWidth: ({ datum }: any) => (datum.highlighted ? 3 : 2),
            },
            labels: victoryPointLabelStyle,
          }}
          labels={({ datum }: any) => `${Math.round(datum.y)}%`}
          labelComponent={<VictoryLabel dy={({ datum }: any) => datum.labelDy ?? -14} style={{ fontSize: 11, fill: '#005CBC', fontFamily: FontFamily.bold }} />}
        />

        <VictoryScatter
          data={incrementLabels}
          size={0}
          labels={({ datum }: any) => datum.label}
          style={{
            labels: victoryIncrementLabelStyle,
          }}
          labelComponent={<VictoryLabel dy={-8} style={{ fontSize: 10, fill: '#0A8A1F', fontFamily: FontFamily.bold }} />}
        />
      </VictoryChart>
      </ScrollView>
    </View>
  );
};

export default PTExecutionTrendChart;
